import { ChangeDetectionStrategy, Component, computed, input, InputSignal, Signal } from '@angular/core';
import { SzRangeBandSession } from './sz-range-band.types';

interface BandRender {
  readonly x: number;
  readonly yTop: number;
  readonly yBottom: number;
  readonly cy: number;
  readonly label: string;
  readonly isLatest: boolean;
}

interface ScaledChart {
  readonly bands: readonly BandRender[];
  readonly yLabels: readonly { y: number; text: string }[];
  readonly trendX1: number;
  readonly trendY1: number;
  readonly trendX2: number;
  readonly trendY2: number;
}

const VIEW_W: number = 360;
const VIEW_H: number = 220;
const PAD_L: number = 30;
const PAD_R: number = 20;
const PAD_T: number = 18;
const PAD_B: number = 32;
const BAND_WIDTH: number = 6;

@Component({
  selector: 'sz-range-band',
  templateUrl: './sz-range-band.component.html',
  styleUrls: ['./sz-range-band.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true
})
export class SzRangeBandComponent {
  readonly sessions: InputSignal<readonly SzRangeBandSession[]> = input.required<readonly SzRangeBandSession[]>();
  readonly lowerIsBetter: InputSignal<boolean> = input<boolean>(true);
  /** Optional fixed y-range; if not provided, derived from data. */
  readonly yMin: InputSignal<number | null> = input<number | null>(null);
  readonly yMax: InputSignal<number | null> = input<number | null>(null);
  readonly yUnit: InputSignal<string> = input<string>('');
  /** Show dashed lime trend line connecting best values. */
  readonly showBestTrend: InputSignal<boolean> = input<boolean>(true);

  readonly viewBox: string = `0 0 ${VIEW_W} ${VIEW_H}`;
  readonly viewW: number = VIEW_W;
  readonly viewH: number = VIEW_H;
  readonly padL: number = PAD_L;
  readonly bandWidth: number = BAND_WIDTH;

  readonly chart: Signal<ScaledChart | null> = computed((): ScaledChart | null => {
    const sessions: readonly SzRangeBandSession[] = this.sessions();
    if (sessions.length === 0) return null;
    const allValues: number[] = sessions.flatMap((s: SzRangeBandSession): number[] => [s.best, s.avg, s.worst]);
    const minY: number = this.yMin() ?? Math.min(...allValues);
    const maxY: number = this.yMax() ?? Math.max(...allValues);
    const yRange: number = maxY - minY || 1;

    const plotW: number = VIEW_W - PAD_L - PAD_R;
    const plotH: number = VIEW_H - PAD_T - PAD_B;
    const step: number = sessions.length > 1 ? plotW / (sessions.length - 1) : 0;

    const toY = (value: number): number => PAD_T + ((maxY - value) / yRange) * plotH;

    const bands: BandRender[] = sessions.map((s: SzRangeBandSession, i: number): BandRender => {
      const x: number = PAD_L + i * step;
      const isLatest: boolean = i === sessions.length - 1;
      const lowerIsBetter: boolean = this.lowerIsBetter();
      const yBest: number = toY(s.best);
      const yWorst: number = toY(s.worst);
      return {
        x,
        yTop: lowerIsBetter ? yBest : yWorst,
        yBottom: lowerIsBetter ? yWorst : yBest,
        cy: toY(s.avg),
        label: s.label,
        isLatest
      };
    });

    const first: BandRender = bands[0]!;
    const last: BandRender = bands[bands.length - 1]!;

    return {
      bands,
      yLabels: [
        { y: PAD_T, text: `${maxY.toFixed(1)}${this.yUnit()}` },
        { y: PAD_T + plotH * 0.5, text: `${((maxY + minY) / 2).toFixed(1)}${this.yUnit()}` },
        { y: PAD_T + plotH, text: `${minY.toFixed(1)}${this.yUnit()}` }
      ],
      trendX1: first.x,
      trendY1: this.lowerIsBetter() ? first.yTop : first.yBottom,
      trendX2: last.x,
      trendY2: this.lowerIsBetter() ? last.yTop : last.yBottom
    };
  });
}
